import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Select } from 'antd';

import ContextBar from '../common/ContextBar';
import EmptyState from '../common/EmptyState';
import { CardOptionLabel } from '../cards/CardVisuals';
import { optionSearchFilter } from '../Input';
import { DataState, Skeleton, deriveDataState } from '../loading';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { getCollectionSchema, ingestRows } from '../../services/ingest';
import { collectionCrumbs } from '../../utils/breadcrumbs';
import { collectionZone } from '../../utils/collectionTimezone';
import { normalizeId } from '../../utils/ids';
import { IMPORT_LABEL, importPath } from '../../utils/importEntry';
import { isBorrowedCard } from '../../utils/linkedRecords';
import { canAddItemsNow, canManage, canWriteCard } from '../../utils/permissions';
import SpreadsheetImporter from './SpreadsheetImporter';

const FAMILY_PREFIX = 'family:';

/**
 * `/collections/:collectionId/import?card=<id>` (KPR-1899): Import a
 * spreadsheet, for anyone who can add items to a card here. What a card takes
 * is read from `GET …/schema`, so an element hidden from this person is not
 * offered, and the cards are the ones ingest accepts here that they may write.
 * A parent lists its sub-collections' cards, and choosing one goes to that
 * collection's own import: a row of one is written there, never here.
 */
export default function SpreadsheetImportPage() {
	const { collectionId } = useParams();
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const { collection, collectionLoading } = useCollectionContext() || {};
	const { cardsById } = useCardDefinitions();
	const [schema, setSchema] = useState(null);
	const [schemaError, setSchemaError] = useState(null);
	const [schemaLoading, setSchemaLoading] = useState(true);
	const [attempt, setAttempt] = useState(0);
	const [chosenId, setChosenId] = useState(null);

	useEffect(() => {
		let live = true;
		setSchemaLoading(true);
		setSchemaError(null);
		getCollectionSchema(collectionId)
			.then(data => { if (live) setSchema(data); })
			.catch(error => { if (live) setSchemaError(error); })
			.finally(() => { if (live) setSchemaLoading(false); });
		return () => { live = false; };
	}, [collectionId, attempt]);

	const writable = useMemo(() => (schema?.cards || [])
		.filter(card => canWriteCard(collection, card.id) && !isBorrowedCard(cardsById?.[normalizeId(card.id)])), [schema, collection, cardsById]);

	const askedFor = normalizeId(params.get('card'));
	useEffect(() => { setChosenId(null); }, [askedFor, collectionId]);
	const wanted = normalizeId(chosenId || askedFor);
	const card = writable.find(entry => normalizeId(entry.id) === wanted) || (writable.length === 1 ? writable[0] : null);

	const familyCards = schema?.familyCards;
	const options = useMemo(() => {
		const own = writable.map(entry => ({
			value: entry.id,
			searchText: entry.name,
			label: <CardOptionLabel card={cardsById?.[normalizeId(entry.id)] || entry} />,
		}));
		if (!familyCards?.length) return own;
		return [
			...own,
			{
				label: 'In a sub-collection',
				options: familyCards.map(entry => ({ value: `${FAMILY_PREFIX}${entry.collection.id}:${entry.id}`, searchText: entry.name, label: `${entry.name} · ${entry.collection.name}` })),
			},
		];
	}, [writable, familyCards, cardsById]);

	const chooseCard = useCallback(value => {
		if (String(value).startsWith(FAMILY_PREFIX)) {
			const [childId, childCard] = String(value).slice(FAMILY_PREFIX.length).split(':');
			navigate(importPath(childId, childCard));
		} else {
			setChosenId(value);
		}
	}, [navigate]);

	const send = useCallback((body, config) => ingestRows(collectionId, body, config), [collectionId]);

	const crumbs = collectionCrumbs(collection, collectionId, IMPORT_LABEL);
	const loading = Boolean(collectionLoading) || schemaLoading;
	const hasData = Boolean(schema && collection);
	const mayImport = canAddItemsNow(collection);
	const anyCard = writable.length > 0 || Boolean(familyCards?.length);

	const picker = (
		<label className="spreadsheet-import__field">
			<span className="micro-label">Card</span>
			<Select
				showSearch
				placeholder="Choose the card these records go on"
				value={card?.id}
				options={options}
				filterOption={optionSearchFilter}
				onChange={chooseCard}
				aria-label="The card these records go on"
				notFoundContent="No card here takes your items."
			/>
		</label>
	);

	return (
		<div className="spreadsheet-import-page">
			<ContextBar breadcrumb={crumbs} title={IMPORT_LABEL} />
			<DataState
				state={deriveDataState({ loading, hasData, error: schemaError })}
				hasData={hasData}
				skeleton={<Skeleton variant="list-row" count={5} />}
				label="this collection's cards"
				onRetry={() => setAttempt(n => n + 1)}
			>
				{hasData && !mayImport ? (
					<EmptyState
						title={collection.status === 'archived' ? 'This collection is archived' : 'You cannot add items here'}
						description={collection.status === 'archived'
							? 'An archived collection is read-only. Restore it to import into it.'
							: 'Ask a manager of this collection for access to add items.'}
						primaryAction={{ label: 'Go to the collection', to: `/collections/${collectionId}` }}
					/>
				) : null}
				{hasData && mayImport && !anyCard ? (
					<EmptyState
						title="No card here takes your items"
						description="Records go on a card you can add to. Ask a manager of this collection for access to one."
						primaryAction={{ label: 'Go to the collection', to: `/collections/${collectionId}` }}
					/>
				) : null}
				{hasData && mayImport && anyCard ? (
					<SpreadsheetImporter
						key={collectionId}
						collectionId={collectionId}
						card={card}
						cardPicker={picker}
						send={send}
						zone={collectionZone(collection)}
						canUndo={canManage(collection)}
					/>
				) : null}
			</DataState>
		</div>
	);
}
